import { createTheme } from '@mui/material/styles'

import {
  brandColors,
  spacingScale,
  surfaceTokens,
  typographyScale,
} from './tokens'

const baseShape = {
  borderRadius: 6,
}

const baseTypography = {
  fontFamily: typographyScale.fontFamily,
  fontWeightLight: typographyScale.weights.light,
  fontWeightRegular: typographyScale.weights.regular,
  fontWeightMedium: typographyScale.weights.medium,
  fontWeightBold: typographyScale.weights.bold,
  h1: {
    fontSize: typographyScale.headings.h1.fontSize,
    lineHeight: typographyScale.headings.h1.lineHeight,
    fontWeight: typographyScale.weights.semibold,
  },
  h2: {
    fontSize: typographyScale.headings.h2.fontSize,
    lineHeight: typographyScale.headings.h2.lineHeight,
    fontWeight: typographyScale.weights.semibold,
  },
  h3: {
    fontSize: typographyScale.headings.h3.fontSize,
    lineHeight: typographyScale.headings.h3.lineHeight,
    fontWeight: typographyScale.weights.semibold,
  },
  h4: {
    fontSize: typographyScale.headings.h4.fontSize,
    lineHeight: typographyScale.headings.h4.lineHeight,
    fontWeight: typographyScale.weights.medium,
  },
  h5: {
    fontSize: typographyScale.headings.h5.fontSize,
    lineHeight: typographyScale.headings.h5.lineHeight,
    fontWeight: typographyScale.weights.medium,
  },
  h6: {
    fontSize: typographyScale.headings.h6.fontSize,
    lineHeight: typographyScale.headings.h6.lineHeight,
    fontWeight: typographyScale.weights.medium,
  },
  body1: {
    fontSize: typographyScale.body.default.fontSize,
    lineHeight: typographyScale.body.default.lineHeight,
    fontWeight: typographyScale.weights.regular,
  },
  body2: {
    fontSize: typographyScale.body.small.fontSize,
    lineHeight: typographyScale.body.small.lineHeight,
    fontWeight: typographyScale.weights.regular,
  },
}

const baseComponents = (theme) => {
  const isLightMode = theme.palette.mode === 'light'

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          textTransform: 'none',
          padding: theme.spacing(1.25, 2),
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          ...(isLightMode && {
            border: `1px solid ${surfaceTokens.border}`,
            boxShadow: surfaceTokens.shadow,
          }),
        },
        rounded: {
          borderRadius: theme.shape.borderRadius * 1.25,
        },
      },
    },
  }
}

const lightPalette = {
  mode: 'light',
  background: {
    default: surfaceTokens.background,
    paper: surfaceTokens.paper,
  },
  divider: surfaceTokens.border,
  primary: {
    main: brandColors.primary.main,
    light: brandColors.primary.light,
    dark: brandColors.primary.dark,
    contrastText: brandColors.primary.contrastText,
  },
  secondary: {
    main: brandColors.secondary.main,
    light: brandColors.secondary.light,
    dark: brandColors.secondary.dark,
    contrastText: brandColors.secondary.contrastText,
  },
  info: {
    main: brandColors.info.main,
    light: brandColors.info.light,
    dark: brandColors.info.dark,
    contrastText: brandColors.info.contrastText,
  },
  success: {
    main: brandColors.success.main,
    light: brandColors.success.light,
    dark: brandColors.success.dark,
    contrastText: brandColors.success.contrastText,
  },
  warning: {
    main: brandColors.warning.main,
    light: brandColors.warning.light,
    dark: brandColors.warning.dark,
    contrastText: brandColors.warning.contrastText,
  },
  error: {
    main: brandColors.error.main,
    light: brandColors.error.light,
    dark: brandColors.error.dark,
    contrastText: brandColors.error.contrastText,
  },
  text: {
    primary: brandColors.neutral.darkest,
    secondary: brandColors.neutral.dark,
  },
}

const darkPalette = {
  mode: 'dark',
  background: {
    default: brandColors.neutral.darker,
    paper: brandColors.neutral.darkest,
  },
  primary: {
    main: brandColors.primary.light,
    dark: brandColors.primary.main,
    light: '#91D6FF',
    contrastText: brandColors.primary.contrastText,
  },
  secondary: {
    main: brandColors.secondary.light,
    dark: brandColors.secondary.main,
    light: '#FF7FD7',
    contrastText: brandColors.secondary.contrastText,
  },
  info: {
    main: brandColors.info.light,
    dark: brandColors.info.main,
    light: '#C2B4FF',
    contrastText: brandColors.info.contrastText,
  },
  success: {
    main: brandColors.success.light,
    dark: brandColors.success.main,
    light: '#9BFFD2',
    contrastText: brandColors.success.contrastText,
  },
  warning: {
    main: brandColors.warning.light,
    dark: brandColors.warning.main,
    light: '#FFE0B0',
    contrastText: brandColors.warning.contrastText,
  },
  error: {
    main: brandColors.error.light,
    dark: brandColors.error.main,
    light: '#FFA6A8',
    contrastText: brandColors.error.contrastText,
  },
  text: {
    primary: brandColors.neutral.lightest,
    secondary: brandColors.neutral.light,
  },
}

export const createCompanyTheme = (variant = 'light', overrides) => {
  const variantPalette = variant === 'dark' ? darkPalette : lightPalette

  const baseTheme = createTheme(
    {
      spacing: spacingScale.unit,
      shape: baseShape,
      typography: baseTypography,
    },
    { palette: variantPalette },
  )

  baseTheme.components = {
    ...(baseTheme.components ?? {}),
    ...baseComponents(baseTheme),
  }

  return overrides ? createTheme(baseTheme, overrides) : baseTheme
}
